import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users } from 'lucide-react';
import Alert from '../common/Alert.js';
import axiosInstance from '../../config/axios.js';
import userService from '../../services/UserService.js';
import { FormLayout, FormField } from '../common/index.js';

const AddUser = () => {
  const [formData, setFormData] = useState({
    username: '',
    name: '',
    email: '',
    contact: '',
    city: '',
    location: '',
    dob: '',
    document_type: 'aadhar',
    document_number: '',
    role_id: '',
    password: '',
    status: 'active',
    monthly_salary: '',
    shipment_commission: '',
  });
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState(null);
  const [documentAttachment, setDocumentAttachment] = useState(null);
  const [documentPreview, setDocumentPreview] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [drivingLicenseAttachment, setDrivingLicenseAttachment] = useState(null);
  const [drivingLicensePreview, setDrivingLicensePreview] = useState(null);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [locationHubs, setLocationHubs] = useState([]);
  const [sendEmail, setSendEmail] = useState(true);
  const navigate = useNavigate();

  const documentTypes = ['aadhar', 'pan', 'driving_license', 'voter_id', 'passport'];
  const userStatuses = ['active', 'inactive'];

  useEffect(() => {
    fetchRoles();
    fetchLocationHubs();
  }, []);

  const fetchLocationHubs = async () => {
    try {
      const response = await axiosInstance.get('/location-hubs?all=true');
      setLocationHubs(response.data.hubs || []);
    } catch (err) {
      Alert.error('Failed to load location hubs');
      console.error('Failed to load location hubs', err);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await axiosInstance.get('/roles');
      setRoles(response.data.data);
    } catch (err) {
      Alert.error('Failed to load roles');
      console.error('Failed to load roles', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleProfilePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 1024 * 1024) {
        Alert.error('Profile photo must be less than 1MB');
        return;
      }
      setProfilePhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDocumentAttachmentChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        Alert.error('Document must be less than 2MB');
        return;
      }
      setDocumentAttachment(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setDocumentPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeProfilePhoto = () => {
    setProfilePhoto(null);
    setProfilePhotoPreview(null);
    const fileInput = document.getElementById('profile-photo-input');
    if (fileInput) fileInput.value = '';
  };

  const removeDocumentAttachment = () => {
    setDocumentAttachment(null);
    setDocumentPreview(null);
    const fileInput = document.getElementById('document-attachment-input');
    if (fileInput) fileInput.value = '';
  };

  const handleDrivingLicenseChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        Alert.error('Driving license must be less than 2MB');
        return;
      }
      setDrivingLicenseAttachment(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setDrivingLicensePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeDrivingLicense = () => {
    setDrivingLicenseAttachment(null);
    setDrivingLicensePreview(null);
    const fileInput = document.getElementById('driving-license-input');
    if (fileInput) fileInput.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const userData = {
        ...formData,
        drivingLicenseAttachment: drivingLicenseAttachment,
      };
      const response = await userService.create(userData, profilePhoto, documentAttachment);

      if (sendEmail && response.data) {
        try {
          const userId = response.data.user_id || response.data.id || response.data.username;

          if (userId) {
            await userService.sendCredentials(userId);
            Alert.success('User created and credentials sent successfully!');
          } else {
            Alert.success('User created successfully!');
          }
        } catch (emailErr) {
          Alert.success('User created successfully!');
          console.error('Failed to send credentials email:', emailErr);
        }
      } else {
        Alert.success('User created successfully!');
      }

      setTimeout(() => navigate('/users'), 1500);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormLayout
      title="Add New User"
      icon={Users}
      onSubmit={handleSubmit}
      submitLabel={loading ? 'Creating...' : 'Create User'}
      loading={loading}
      onCancel={() => navigate('/users')}
      maxWidth="max-w-7xl"
    >
      <div>
        <h3 className="text-base font-semibold text-tertiary mb-4 pb-2 border-b border-gray-100">
          Roles & Payment Info
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
          <div className="flex flex-col sm:flex-row items-center sm:items-center gap-4 sm:gap-6 sm:col-span-1">
            <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full border border-gray-300 overflow-hidden bg-gray-100 flex items-center justify-center flex-shrink-0">
              {profilePhotoPreview ? (
                <img src={profilePhotoPreview} alt="Profile Preview" className="w-full h-full object-cover" />
              ) : (
                <span className="text-3xl sm:text-4xl text-gray-400">👤</span>
              )}
            </div>
            <FormField label="Upload Profile Photo" className="flex-1 w-full">
              <input
                id="profile-photo-input"
                type="file"
                accept="image/*"
                onChange={handleProfilePhotoChange}
                className="input-clean py-1"
              />
              <p className="text-xs text-gray-600 mt-1">Accepted formats: JPG, PNG, GIF, WebP (Max 1MB)</p>
              {profilePhotoPreview && (
                <button
                  type="button"
                  onClick={removeProfilePhoto}
                  className="mt-2 text-xs sm:text-sm text-red-600 hover:text-red-700"
                >
                  Remove Photo
                </button>
              )}
            </FormField>
          </div>

          <FormField label="Role" required>
            <select
              name="role_id"
              value={formData.role_id}
              onChange={handleChange}
              className="input-clean"
              required
            >
              <option value="">Select Role</option>
              {roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.role_name}
                </option>
              ))}
            </select>
          </FormField>

          <FormField label="Status">
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="input-clean"
            >
              {userStatuses.map((status) => (
                <option key={status} value={status}>
                  {status.toUpperCase()}
                </option>
              ))}
            </select>
          </FormField>

          <FormField label="Shipment Commission %">
            <input
              type="number"
              name="shipment_commission"
              value={formData.shipment_commission}
              onChange={handleChange}
              placeholder="0"
              min="0"
              max="100"
              className="input-clean"
            />
          </FormField>
        </div>
      </div>

      <div>
        <h3 className="text-base font-semibold text-tertiary mb-4 pb-2 border-b border-gray-100">
          Basic Information
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
          <FormField label="Username" required>
            <input
              type="text"
              name="username"
              value={formData.username}
              onChange={handleChange}
              placeholder="Enter username"
              className="input-clean"
              required
            />
          </FormField>

          <FormField label="Full Name" required>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter full name"
              className="input-clean"
              required
            />
          </FormField>

          <FormField label="Email" required>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter email"
              className="input-clean"
              required
            />
          </FormField>

          <FormField label="Password" required>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter password"
                className="input-clean pr-10"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600 hover:text-gray-900 text-sm"
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
          </FormField>
        </div>
      </div>

      <div>
        <h3 className="text-base font-semibold text-tertiary mb-4 pb-2 border-b border-gray-100">
          Contact Information
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
          <FormField label="Contact Number" required>
            <input
              type="text"
              name="contact"
              value={formData.contact}
              onChange={handleChange}
              placeholder="Enter contact number"
              className="input-clean"
              required
            />
          </FormField>

          <FormField label="Date of Birth">
            <input
              type="date"
              name="dob"
              value={formData.dob}
              onChange={handleChange}
              className="input-clean"
            />
          </FormField>

          <FormField label="Branch" required>
            <select
              name="city"
              value={formData.city}
              onChange={handleChange}
              className="input-clean"
              required
            >
              <option value="">Select City</option>
              {locationHubs.map((hub) => (
                <option key={hub.hub_id} value={hub.location}>
                  {hub.location}
                </option>
              ))}
            </select>
          </FormField>

          <FormField label="Location" required>
            <input
              type="text"
              name="location"
              value={formData.location}
              onChange={handleChange}
              placeholder="Enter location/address"
              className="input-clean"
              required
            />
          </FormField>
        </div>
      </div>

      <div>
        <h3 className="text-base font-semibold text-tertiary mb-4 pb-2 border-b border-gray-100">
          Document Information
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
          <FormField label="Document Type" required>
            <select
              name="document_type"
              value={formData.document_type}
              onChange={handleChange}
              className="input-clean"
              required
            >
              {documentTypes.map((type) => (
                <option key={type} value={type}>
                  {type.replace(/_/g, ' ').toUpperCase()}
                </option>
              ))}
            </select>
          </FormField>

          <FormField label="Document Number" required>
            <input
              type="text"
              name="document_number"
              value={formData.document_number}
              onChange={handleChange}
              placeholder="Enter document number"
              className="input-clean"
              required
            />
          </FormField>

          <FormField label="Document Attachment" className="sm:col-span-2">
            <input
              id="document-attachment-input"
              type="file"
              accept="image/*"
              onChange={handleDocumentAttachmentChange}
              className="input-clean py-1"
            />
            <p className="text-xs text-gray-600 mt-1">Upload scanned copy of the document (Max 2MB)</p>
            {documentPreview && (
              <div className="mt-3">
                <div className="relative inline-block">
                  <img
                    src={documentPreview}
                    alt="Document Preview"
                    className="w-32 h-32 sm:w-40 sm:h-40 object-cover border border-gray-300 rounded-md"
                  />
                  <button
                    type="button"
                    onClick={removeDocumentAttachment}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors text-sm"
                    title="Remove document"
                  >
                    ×
                  </button>
                </div>
              </div>
            )}
          </FormField>
        </div>
      </div>

      {formData.role_id &&
        roles.find((r) => r.id === parseInt(formData.role_id))?.role_name === 'Driver' && (
          <div>
            <h3 className="text-base font-semibold text-tertiary mb-4 pb-2 border-b border-gray-100">
              Driving License
            </h3>
            <FormField label="Driving License Attachment" className="sm:w-1/2">
              <input
                id="driving-license-input"
                type="file"
                accept="image/*"
                onChange={handleDrivingLicenseChange}
                className="input-clean"
              />
              <p className="text-xs text-gray-600 mt-1">Upload driving license (Max 2MB)</p>
              {drivingLicensePreview && (
                <div className="mt-3">
                  <div className="relative inline-block">
                    <img
                      src={drivingLicensePreview}
                      alt="License Preview"
                      className="w-32 h-32 sm:w-40 sm:h-40 object-cover border border-gray-300 rounded-md"
                    />
                    <button
                      type="button"
                      onClick={removeDrivingLicense}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors text-sm"
                      title="Remove license"
                    >
                      ×
                    </button>
                  </div>
                </div>
              )}
            </FormField>
          </div>
        )}

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="sendEmail"
          checked={sendEmail}
          onChange={(e) => setSendEmail(e.target.checked)}
          className="w-4 h-4 text-brand focus:ring-brand border-gray-300 rounded"
        />
        <label htmlFor="sendEmail" className="text-sm text-gray-900">
          Send login credentials to user&apos;s email
        </label>
      </div>
    </FormLayout>
  );
};

export default AddUser;
